import { Site } from '@/types/site';
import { Zone } from '@/types/zone';
import { System } from '@/types/system';
import { Endpoint } from '@/types/endpoint';
import { Link } from '@/types/link';

export interface SiteMetrics {
  siteId: string;
  siteName: string;
  zoneCount: number;
  systemCount: number;
  endpointCount: number;
  riskCount: number;
  incidentCount: number;
  controlCount: number;
  taskCount: number;
  overallRiskLevel: 'low' | 'medium' | 'high' | 'critical';
}

interface Risk {
  id: string;
  status?: string | null;
  impact?: number | null;
  likelihood?: number | null;
}

interface Control {
  id: string;
  status?: string | null;
}

interface Incident {
  id: string;
  status: string;
  priority: string;
}

interface Task {
  id: string;
  status: string;
}

export function calculateSiteMetrics(
  site: Site,
  zones: Zone[],
  systems: System[],
  endpoints: Endpoint[],
  links: Link[],
  risks: Risk[] = [],
  controls: Control[] = [],
  incidents: Incident[] = [],
  tasks: Task[] = []
): SiteMetrics {
  // Get zones for this site
  const siteZones = zones.filter(z => z.site_id === site.id);
  const zoneIds = siteZones.map(z => z.id);

  // Get systems in these zones
  const siteSystems = systems.filter(s => zoneIds.includes(s.zone_id));
  const systemIds = siteSystems.map(s => s.id);

  // Get endpoints (direct site endpoints + system endpoints)
  const siteEndpoints = endpoints.filter(
    e => e.site_id === site.id || (e.system_id && systemIds.includes(e.system_id))
  );
  const endpointIds = siteEndpoints.map(e => e.id);

  // Collect all entity IDs that belong to this site
  const allEntityIds = new Set([
    site.id,
    ...zoneIds,
    ...systemIds,
    ...endpointIds,
  ]);

  // Count risks linked to any site entity (open only)
  const siteRisks = risks.filter(r => {
    if (r.status === 'Closed') return false;
    // Check if risk is directly related to any of our entities via links
    const linkedToSite = links.some(
      link =>
        link.source_type === 'risk' &&
        link.source_id === r.id &&
        allEntityIds.has(link.target_id)
    ) || links.some(
      link =>
        link.target_type === 'risk' &&
        link.target_id === r.id &&
        allEntityIds.has(link.source_id)
    );
    return linkedToSite;
  });

  // Count controls linked to site (with gaps)
  const siteControls = controls.filter(c => {
    if (c.status === 'implemented') return false;
    const linkedToSite = links.some(
      link =>
        link.source_type === 'control' &&
        link.source_id === c.id &&
        allEntityIds.has(link.target_id)
    ) || links.some(
      link =>
        link.target_type === 'control' &&
        link.target_id === c.id &&
        allEntityIds.has(link.source_id)
    );
    return linkedToSite;
  });

  // Count incidents linked to site (open only)
  const siteIncidents = incidents.filter(i => {
    if (i.status === 'closed') return false;
    const linkedToSite = links.some(
      link =>
        link.source_type === 'incident' &&
        link.source_id === i.id &&
        allEntityIds.has(link.target_id)
    ) || links.some(
      link =>
        link.target_type === 'incident' &&
        link.target_id === i.id &&
        allEntityIds.has(link.source_id)
    );
    return linkedToSite;
  });

  // Count tasks linked to site (pending only)
  const siteTasks = tasks.filter(t => {
    if (t.status === 'Done' || t.status === 'Skipped') return false;
    const linkedToSite = links.some(
      link =>
        link.source_type === 'task' &&
        link.source_id === t.id &&
        allEntityIds.has(link.target_id)
    ) || links.some(
      link =>
        link.target_type === 'task' &&
        link.target_id === t.id &&
        allEntityIds.has(link.source_id)
    );
    return linkedToSite;
  });

  // Calculate overall risk level based on risks
  let overallRiskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
  
  const highRisks = siteRisks.filter(r => {
    const score = (r.impact ?? 0) * (r.likelihood ?? 0);
    return score >= 16;
  });
  
  const mediumRisks = siteRisks.filter(r => {
    const score = (r.impact ?? 0) * (r.likelihood ?? 0);
    return score >= 9 && score < 16;
  });

  if (highRisks.length > 0) {
    overallRiskLevel = 'critical';
  } else if (siteRisks.length > 5) {
    overallRiskLevel = 'high';
  } else if (mediumRisks.length > 0 || siteRisks.length > 2) {
    overallRiskLevel = 'medium';
  }

  return {
    siteId: site.id,
    siteName: site.name,
    zoneCount: siteZones.length,
    systemCount: siteSystems.length,
    endpointCount: siteEndpoints.length,
    riskCount: siteRisks.length,
    incidentCount: siteIncidents.length,
    controlCount: siteControls.length,
    taskCount: siteTasks.length,
    overallRiskLevel,
  };
}

export function getRiskLevelColor(level: 'low' | 'medium' | 'high' | 'critical'): string {
  switch (level) {
    case 'critical':
      return '#ef4444'; // red-500
    case 'high':
      return '#f97316'; // orange-500
    case 'medium':
      return '#f59e0b'; // amber-500
    case 'low':
    default:
      return '#10b981'; // green-500
  }
}
